"use client";
import React, { ReactNode } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useCarousel } from "@/hooks/utility/useCarousel";
import { FeaturedReview } from "@/types/api/home";
import ReviewCard from "../review-card/ReviewCard";

interface ReviewsCarouselProps {
  background?: string;
  reviews: FeaturedReview[];
  intervalMs?: number;
  containerClassName?: string;
  carouselClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  cardClassName?: string;
  selectedCardClassName?: string;
  unselectedCardClassName?: string;
  titleComponent: ReactNode;
}

function ReviewsCarousel({
  reviews,
  intervalMs = 4000,
  titleComponent,
  background = "bg-white",
  containerClassName = "",
  carouselClassName = "h-[500px] sm:h-[550px] md:h-[580px] lg:h-[650px]",
  contentClassName = "pt-14 pb-8",
  itemClassName = "flex-shrink-0 flex-grow-0 basis-[65%] pl-0 xs:basis-[50%] md:basis-[33.333%] lg:basis-[30%] xl:basis-[25%]",
  cardClassName = "mx-auto rounded-none border-10 border-white",
  selectedCardClassName = "h-[284px] w-[227px] sm:h-[340px] sm:w-[272px] lg:h-[420px] lg:w-[337px]",
  unselectedCardClassName = "h-[250px] w-[200px] !shadow-[var(--shadow-card_02)] sm:h-[284px] sm:w-[227px] lg:h-[357px] lg:w-[286px] top-4 md:top-7",
}: ReviewsCarouselProps) {
  const { currentIndex: selected, setCarouselApi: setApi } = useCarousel();
  if (reviews.length === 0) return null;
  return (
    <div className={`w-full pb-8 ${background} ${containerClassName} `}>
      <div className="pt-8 md:pt-12">
        <div className="app-container">{titleComponent}</div>
        <Carousel
          opts={{ loop: true, align: "center" }}
          plugins={[Autoplay({ delay: intervalMs, stopOnInteraction: false })]}
          setApi={setApi}
          className={carouselClassName}>
          <CarouselContent className={contentClassName}>
            {reviews.map((t, index) => {
              const isSelected = index === selected;
              return (
                <CarouselItem
                  key={`review-${t.id}-${index}`}
                  className={itemClassName}>
                  <ReviewCard
                    name={t.travelers?.join(" & ") || ""}
                    location={t.destination}
                    image={t.image}
                    comment={t.comment}
                    isSelected={isSelected}
                    destination={t.destination}
                    date={`${t.month} ${t.year}`}
                    rating={t.rating}
                    className={`!shadow-[var(--shadow-card_02)] transition-all duration-500 hover:shadow-none ${
                      isSelected
                        ? selectedCardClassName
                        : unselectedCardClassName
                    } ${cardClassName}`}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}

export default ReviewsCarousel;
